import { callServiceAPI } from '@/lib/firebase/functions';
import type { HttpsCallable } from 'firebase/functions';
import type { APIResponse } from '@/types/common';
import { useAuthStore, getEffectiveUid } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { useUIStore } from '@/stores/ui-store';
import { networkStats } from '@/lib/network-stats';
import { idbCache } from '@/lib/cache/idb-cache';
import { isPreservedCacheKey } from '@/lib/cache/scoped-cache';
import { enforceRateLimit, enterBulkMode as _enterBulkMode, isBulkModeActive as _isBulkModeActive } from '@/lib/rate-guard';
import { clearAllEncryptedTokens } from '@/lib/api/encrypted-token';

// Re-export so existing call sites (`bulk-sender.tsx`, `message-store.ts`)
// keep working without import-path churn. The actual implementation now
// lives in `rate-guard.ts` so EVERY `enforceRateLimit` consumer (Firestore
// + CF) honours the bypass — not just the proxy.
export const enterBulkMode = _enterBulkMode;
export const isBulkModeActive = _isBulkModeActive;

interface ProxyRequestOptions {
  appCode: string;
  url: string;
  method: string;
  data?: unknown;
  headers?: Record<string, string>;
  resource?: string;
  /** TPA service name (e.g. 'twilio'). When set, the Cloud Function
   *  loads TPA credentials from Firestore and injects auth headers. */
  TPAService?: string;
  /** Optional override callable. When provided, this callable is used
   *  instead of the default `callServiceAPI`. Used by Calendly to route
   *  proxy calls through the local emulator in dev. */
  callable?: HttpsCallable<unknown, unknown>;
  /** When true, bypass client-side cache for this request (read & write). */
  skipCache?: boolean;
}

/**
 * Resolve the actual HTTP method from a cache-prefixed method string.
 *
 * The client uses extended method names like GET_CACHED_ONE_DAY, POST_CACHED_ONE_HOUR etc.
 * to signal that the response should be cached client-side. The Cloud Function only
 * understands standard HTTP methods (GET, POST, PUT, DELETE, OPTIONS), so we strip
 * the cache suffix before sending.
 *
 * This mirrors the original app's firebase-things.js:
 *   method = method.includes('GET') ? 'GET' : method;
 *   method = method.includes('POST') ? 'POST' : method;
 */
function resolveHTTPMethod(method: string): string {
  if (method.includes('GET')) return 'GET';
  if (method.includes('POST')) return 'POST';
  if (method.includes('PUT')) return 'PUT';
  if (method.includes('DELETE')) return 'DELETE';
  if (method.includes('OPTIONS')) return 'OPTIONS';
  return method;
}

/**
 * Determine the cache TTL from a method string.
 *
 * All GET requests get at least a 1-second dedup cache to prevent duplicate
 * calls from re-renders or parallel mounts hitting the CF multiple times.
 * The rate guard catches infinite loops, but this avoids wasting CF calls
 * for harmless duplicates within the same second.
 */
function getCacheTTL(method: string): number | null {
  if (method.includes('CACHED_ONE_MIN')) return 60 * 1000;           // 1 minute
  if (method.includes('CACHED_ONE_HOUR')) return 60 * 60 * 1000;     // 1 hour
  if (method.includes('CACHED_ONE_WEEK')) return 7 * 24 * 60 * 60 * 1000; // 1 week
  if (method.includes('CACHED')) return 24 * 60 * 60 * 1000;       // 1 day (default)
  if (method === 'GET') return 2_000;                               // 2 second dedup
  return null;
}

/**
 * Temporarily bypass cache for all subsequent proxy requests.
 * Call `setSkipCacheOnce(true)` before a batch of API calls that must be fresh,
 * then it auto-resets after the next proxy request completes.
 */
let _skipCacheOnce = false;
export function setSkipCacheOnce(skip: boolean): void { _skipCacheOnce = skip; }


/** Cache entry — stores API data + optional CF metadata (e.g. availableWABAIds). */
interface CacheEntry {
  data: unknown;
  expiresAt: number;
  /** CF envelope metadata (e.g. availableWABAIds, currentResultWABAId).
   *  Preserved so cache hits return the same additionalData as fresh calls. */
  additionalData?: Record<string, unknown>;
  /** Envelope-level api_domain (e.g. "www.zohoapis.eu"). Cached so a hit
   *  still lets ZohoOAuthProcessor sync currentDomain on the first call. */
  apiDomain?: string;
}

/** Simple in-memory + localStorage response cache (mirrors original app). */
const responseCache = new Map<string, CacheEntry>();

/**
 * Periodic sweep of stale in-memory cache entries.
 * Runs every 30 seconds — removes entries whose expiresAt has passed.
 * This keeps the L1 Map from growing unbounded (especially with 1s dedup entries).
 */
const SWEEP_INTERVAL_MS = 30_000;
setInterval(() => {
  const now = Date.now();
  let swept = 0;
  for (const [key, entry] of responseCache) {
    if (now >= entry.expiresAt) {
      responseCache.delete(key);
      swept++;
    }
  }
  if (swept > 0) {
    console.log(`[Proxy Cache] Swept ${swept} stale entries, ${responseCache.size} remaining`);
  }
}, SWEEP_INTERVAL_MS);

function getCacheKey(appCode: string, url: string, data?: unknown): string {
  const stringData = data != null ? JSON.stringify(data) : '';
  // Scope the key by effective uid + appCode. Different apps (e.g.
  // calendlyforzohocrm vs calendlyforzohodesk) call byte-identical provider
  // URLs (/users/me, /event_types, …) with DIFFERENT credentials — a
  // URL-only key served one app's cached response to the other for the
  // whole TTL (cross-app data leak). Mirrors the {uid}:{appCode} scoping
  // already applied to the localStorage caches (scoped-cache.ts).
  // uid may be '' very early in init — that entry is simply never hit
  // again once auth resolves, which is a cache miss, not a leak.
  const uid = getEffectiveUid() || '';
  // Use encodeURIComponent to handle non-Latin1 characters safely
  // (btoa throws on characters outside the Latin1 range)
  return btoa(encodeURIComponent(`${uid}::${appCode}::${url}${stringData}`));
}

// Short-TTL threshold — entries below this only live in L1 memory (skip IDB).
// Prevents 1-second dedup entries from polluting IDB and avoids reading
// stale longer-lived IDB entries when a method is downgraded to plain GET.
const L1_ONLY_TTL_MS = 10_000; // 10 seconds

async function getCachedResponse<T>(key: string, l1Only = false): Promise<{ data: T; additionalData?: Record<string, unknown>; apiDomain?: string } | null> {
  // L1: Check in-memory first (zero-latency)
  const memEntry = responseCache.get(key);
  if (memEntry && Date.now() < memEntry.expiresAt) {
    return { data: memEntry.data as T, additionalData: memEntry.additionalData, apiDomain: memEntry.apiDomain };
  }
  // Expired L1 entry — delete immediately
  if (memEntry) {
    responseCache.delete(key);
  }
  // Short-TTL (dedup) calls only check L1 — skip IDB/localStorage
  if (l1Only) return null;
  // L2: Check IndexedDB
  const idbData = await idbCache.get<CacheEntry>('proxy_cache', key);
  if (idbData) {
    // Re-populate L1 in-memory cache
    responseCache.set(key, idbData);
    return { data: idbData.data as T, additionalData: idbData.additionalData, apiDomain: idbData.apiDomain };
  }
  // Legacy: Check localStorage (will be removed after migration period)
  try {
    const raw = localStorage.getItem(`ua_cache_${key}`);
    if (raw) {
      const parsed = JSON.parse(raw) as CacheEntry;
      if (parsed.expiresAt && Date.now() < parsed.expiresAt) {
        // Migrate to IDB + L1
        responseCache.set(key, parsed);
        const remainingTtl = parsed.expiresAt - Date.now();
        idbCache.set('proxy_cache', key, parsed, remainingTtl).catch(() => {});
        // Remove from localStorage after migration
        localStorage.removeItem(`ua_cache_${key}`);
        return { data: parsed.data as T, additionalData: parsed.additionalData, apiDomain: parsed.apiDomain };
      }
      // Expired — clean up
      localStorage.removeItem(`ua_cache_${key}`);
    }
  } catch {
    // localStorage access can fail (e.g. in private browsing)
  }
  return null;
}

function setCachedResponse(key: string, data: unknown, ttlMs: number, additionalData?: Record<string, unknown>, apiDomain?: string): void {
  const entry: CacheEntry = { data, expiresAt: Date.now() + ttlMs, additionalData, apiDomain };
  // L1: in-memory
  responseCache.set(key, entry);
  // L2: IndexedDB — only for longer-lived entries (skip dedup/short TTLs)
  if (ttlMs > L1_ONLY_TTL_MS) {
    idbCache.set('proxy_cache', key, entry, ttlMs).catch(() => {});
  }
}

/**
 * Clear all proxy response cache (both in-memory and localStorage).
 * Useful when checking auth status and need a fresh response.
 *
 * Also flushes the encrypted-token slots — most callers of this function
 * (TPA reauthorize, settings "clear cache", incoming-sync changes) imply
 * that stored credentials may no longer be valid, so the next CF call
 * should re-load fresh creds rather than ride the stale cached token.
 */
export function clearProxyCache(): void {
  responseCache.clear();
  clearAllEncryptedTokens();
  // Clear IDB proxy_cache store
  idbCache.clear('proxy_cache').catch(() => {});
  // Legacy: clean up any remaining localStorage proxy cache entries.
  // Preserve app-level caches that live in localStorage for sync access.
  const preserveKeys = new Set([
    'ua_cache_user', 'ua_cache_user_timestamp',
  ]);
  try {
    Object.keys(localStorage)
      // The app caches (org id / CRM user id / trial start) are preserved via
      // isPreservedCacheKey rather than an exact-name set — they now carry a
      // `:{uid}:{appCode}` suffix, which an equality check would miss and
      // silently delete on every reauthorize.
      .filter((key) => key.startsWith('ua_cache_') && !preserveKeys.has(key) && !isPreservedCacheKey(key))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // localStorage access can fail
  }
}

/**
 * Nuclear cache clear — wipes ALL caches including app-level localStorage keys.
 * Used by the "Clear Cache" setting. After this, the next page load will
 * fetch everything fresh from Firestore/API.
 */
export function clearAllCache(): void {
  clearProxyCache();
  // Also wipe the preserved app-level keys
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith('ua_cache_'))
      .forEach((key) => localStorage.removeItem(key));
  } catch { /* */ }
}

/**
 * Clear cache for a specific URL pattern (selective cache invalidation).
 * Useful when you only want to invalidate auth-related endpoints without
 * clearing all cached data.
 */
export function clearProxyCacheByUrl(urlPattern: string): void {
  // Clear from in-memory cache
  for (const [key] of responseCache.entries()) {
    try {
      const decoded = atob(key);
      if (decoded.includes(urlPattern)) {
        responseCache.delete(key);
      }
    } catch {
      // Invalid base64, skip
    }
  }

  // Clear from IDB proxy_cache by URL pattern
  idbCache.clearByKeyPattern('proxy_cache', urlPattern).catch(() => {});

  // Legacy: Clear from localStorage
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith('ua_cache_'))
      .forEach((key) => {
        try {
          const cacheKey = key.replace('ua_cache_', '');
          const decoded = atob(cacheKey);
          if (decoded.includes(urlPattern)) {
            localStorage.removeItem(key);
          }
        } catch {
          // Invalid base64 or access error, skip
        }
      });
  } catch {
    // localStorage access can fail
  }
}

/**
 * Make a proxy request through the Cloud Function.
 * This replaces the old /v1/proxyrequest pattern and handles
 * credential injection server-side.
 *
 * For TPA calls, TPAService MUST be set so the Cloud Function
 * knows to fetch TPA credentials from:
 *   ulgebraUsers/{uid}/extensions/{appCode}/tokens/tpa
 */
export async function proxyRequest<T = unknown>(
  options: ProxyRequestOptions
): Promise<APIResponse<T>> {
  // Skip cache when settings page is open, skipCache option set, or global skip flag
  const isSettingsOpen = useUIStore.getState().settingsOpen;
  const bypassCache = isSettingsOpen || options.skipCache === true || _skipCacheOnce;

  // Check client-side cache for CACHED methods
  const cacheTTL = getCacheTTL(options.method);
  if (cacheTTL && !bypassCache) {
    const cacheKey = getCacheKey(options.appCode, options.url, options.data);
    const l1Only = cacheTTL <= L1_ONLY_TTL_MS;
    const cachedResult = await getCachedResponse<Record<string, unknown>>(cacheKey, l1Only);
    if (cachedResult) {
      console.log('[Proxy] ✓ Cache HIT:', options.url, cachedResult.additionalData ? '(+additionalData)' : '');
      networkStats.cacheHit();
      const cachedData = cachedResult.data;
      return {
        success: true,
        data: cachedData as T,
        statusCode: (cachedData.code ?? cachedData.status ?? 200) as number,
        additionalData: cachedResult.additionalData,
        apiDomain: cachedResult.apiDomain,
      };
    }
    console.log('[Proxy] ✗ Cache MISS:', options.url, 'TTL:', cacheTTL / 1000, 's');
  }

  // Resolve the actual HTTP method (strip CACHED suffix)
  const httpMethod = resolveHTTPMethod(options.method);

  // Rate guard — block if the same URL fires >300 times/min (possible
  // infinite loop). `enforceRateLimit` itself short-circuits while a bulk
  // operation is in flight, so we don't need to gate the call here.
  try {
    enforceRateLimit(`CF:${options.url}`, `CF ${httpMethod} ${options.url}`, 300);
  } catch (rateLimitError) {
    return {
      success: false,
      error: rateLimitError instanceof Error ? rateLimitError.message : 'Rate limit exceeded',
    };
  }

  try {
    // Inject impersonation / shared-admin params
    let { impersonatedUserId, selectedAdminUserId } = useAuthStore.getState();

    // Safety fallback: if store hasn't been hydrated yet, read directly from localStorage
    if (!selectedAdminUserId) {
      try {
        const ext = useAppStore.getState().extensionName;
        const uid = useAuthStore.getState().user?.uid;
        if (ext && uid) {
          const cached = localStorage.getItem(
            `UA_LIC_UTILITY___CURRENT_SELECTED_ORG_ADMIN_USER_ID_${ext}_${uid}`
          );
          if (cached) {
            selectedAdminUserId = cached;
            console.log('[Proxy] selectedAdminUserId from localStorage fallback:', cached);
          }
        }
      } catch { /* localStorage unavailable */ }
    }

    console.log('[Proxy] →', httpMethod, options.url, options.TPAService ? `TPA=${options.TPAService}` : '', options.callable ? '(local)' : '', impersonatedUserId ? `IMPERSONATE=${impersonatedUserId}` : '', selectedAdminUserId ? `ADMIN=${selectedAdminUserId}` : '');
    networkStats.cacheMiss();
    const callFn = options.callable ?? callServiceAPI;
    const result = await callFn({
      appCode: options.appCode,
      url: options.url,
      method: httpMethod,
      data: options.data,
      headers: options.headers ?? {},
      resource: options.resource,
      TPAService: options.TPAService,
      ...(impersonatedUserId ? { _ua_lic_impersonateUserID: impersonatedUserId } : {}),
      ...(selectedAdminUserId ? { _ua_lic_adminUserID: selectedAdminUserId } : {}),
    });

    // The Cloud Function (via Firebase httpsCallable) returns a response envelope:
    //   result.data = { data: <actual API response>, status, code, error, message, encryptedToken, ... }
    //
    // For example, a Twilio Accounts call returns:
    //   result.data = { data: { accounts: [...] }, status: 200, encryptedToken: "..." }
    //
    // The original app (firebase-things.js line 8810) does:
    //   response = response.data;       // unwrap httpsCallable wrapper → envelope
    //   returnResponse = response;       // consumers then access resp.data.accounts
    //
    // `encryptedToken` itself is intercepted upstream by `withEncryptedTokens`
    // in `lib/firebase/functions.ts` — it's stashed and re-injected as the
    // `UA-Authorization-Encoded` header on the next call so the CF can skip
    // a Firestore credential read. Consumers don't need to do anything.
    //
    // Our TPA modules expect APIResponse.data to be the actual API response (e.g. { accounts: [...] }).
    // So we unwrap envelope.data here to give consumers direct access.
    const envelope = result.data as Record<string, unknown>;

    // Extract status and error from the envelope level
    const statusCode = (envelope?.code ?? envelope?.status ?? 200) as number;
    const errorData = envelope?.error as Record<string, unknown> | string | undefined;

    // The actual API response payload lives inside envelope.data
    // If envelope.data doesn't exist, fall back to the envelope itself (some internal actions return flat)
    const apiData = (envelope?.data ?? envelope) as T;
    console.log('[Proxy] ←', httpMethod, options.url, 'status:', statusCode, 'error:', errorData ?? 'none', 'apiData keys:', apiData && typeof apiData === 'object' ? Object.keys(apiData as Record<string, unknown>) : 'raw');

    // Preserve additionalData from the Cloud Function envelope (e.g. availableWABAIds)
    const additionalData = envelope?.additionalData as Record<string, unknown> | undefined;

    // Envelope-level api_domain — the proxy CF (sms-functions.js
    // new_callServiceAuthorizedAPI) injects this on every Zoho/Pipedrive/etc.
    // response so the client can resolve the user's data center without a
    // separate Firestore round-trip. Surfaced via APIResponse.apiDomain
    // so consumers (e.g. ZohoOAuthProcessor) can sync currentDomain.
    const apiDomain = typeof envelope?.api_domain === 'string' ? envelope.api_domain : undefined;

    if (errorData || statusCode >= 400) {
      // Prefer Meta's user-friendly error fields (error_user_title / error_user_msg)
      // over the generic "Invalid parameter" message.
      // Meta errors can be nested: { error: { error: { message, error_user_title, ... } } }
      // or flat: { error: { message, error_user_title, ... } }
      let errorMessage: string;
      let errorTitle: string | undefined;
      if (typeof errorData === 'string') {
        errorMessage = errorData;
      } else if (errorData) {
        // Resolve the innermost error object (handles nested { error: { error: { ... } } })
        const innerError = (typeof errorData.error === 'object' && errorData.error !== null)
          ? errorData.error as Record<string, unknown>
          : errorData;
        const userTitle = innerError.error_user_title as string | undefined;
        const userMsg = innerError.error_user_msg as string | undefined;
        // Also check error_data.details (e.g. payment setup errors from Meta)
        const errorDataObj = innerError.error_data as Record<string, unknown> | undefined;
        const errorDetails = errorDataObj?.details as string | undefined;
        if (errorDetails) {
          // error_data.details has the most specific message (e.g. payment not set up)
          errorTitle = userTitle;
          errorMessage = errorDetails;
        } else if (userTitle || userMsg) {
          errorTitle = userTitle;
          errorMessage = userMsg || userTitle!;
        } else {
          errorMessage = (innerError.message as string) ?? (errorData.message as string) ?? (envelope?.message as string) ?? 'Request failed';
        }
      } else {
        errorMessage = (envelope?.message as string) ?? 'Request failed';
      }
      return {
        success: false,
        data: apiData,
        error: errorMessage,
        errorTitle,
        statusCode,
        additionalData,
        apiDomain,
      };
    }

    // Cache successful responses for CACHED methods (skip when bypassing cache)
    if (cacheTTL && !errorData && statusCode < 400 && !bypassCache) {
      const cacheKey = getCacheKey(options.appCode, options.url, options.data);
      setCachedResponse(cacheKey, apiData, cacheTTL, additionalData, apiDomain);
      console.log('[Proxy] ✓ Cache SET:', options.url, 'TTL:', cacheTTL / 1000, 's', additionalData ? '(+additionalData)' : '');
    }

    return {
      success: true,
      data: apiData,
      statusCode,
      additionalData,
      apiDomain,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Proxy request failed',
    };
  }
}
